import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Lightning, Warning, Sun, Moon } from '@phosphor-icons/react';
import LanguageSelector from '../components/LanguageSelector';

export default function SigninPage() {
  const { t } = useTranslation();
  const { login } = useSaaSAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/app');
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6" data-testid="signin-page">
      <div className="absolute right-6 top-6 flex items-center gap-3"><LanguageSelector className="w-32" /><button onClick={toggleTheme} className="text-muted-foreground hover:text-foreground">{theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}</button></div>
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Lightning size={24} weight="fill" className="text-foreground" />
          <span className="font-heading text-lg font-bold text-foreground">Mayr Lead Creator</span>
        </Link>
        <h1 className="font-heading text-2xl font-bold text-foreground mb-1">{t('auth.signin')}</h1>
        <p className="text-sm text-muted-foreground mb-6">{t('auth.signinSubtitle')}</p>

        {error && <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm"><Warning size={16} />{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1 block">{t('auth.email')}</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="signin-email" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1 block">{t('auth.password')}</label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="signin-password" />
          </div>
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground underline">{t('auth.forgot')}</Link>
          </div>
          <Button type="submit" disabled={loading} className="w-full" data-testid="signin-submit">{loading ? t('auth.signingIn') : t('auth.signin')}</Button>
        </form>
        <p className="text-xs text-muted-foreground mt-4 text-center">
          {t('auth.newAccount')} <Link to="/signup" className="text-foreground underline">{t('auth.startInProEmail')}</Link>
        </p>
      </div>
    </div>
  );
}
